/** @jsxImportSource theme-ui */
import { FormikProps } from 'formik';
import { NumericDictionary } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

import * as dto from 'client/dto';
import { sxtheme } from 'utils/cssHelpers';
import { nameof } from 'utils/typeHelpers';

import { FlexGroup } from 'components/_common/FlexGroup';
import { Typography } from 'components/_common/Typography';
import { BdDropdown } from 'components/_controls/BdDropdown/BdDropdown';
import { BdSearch } from 'components/_controls/BdSearch';

import { Map2DiseaseGroupingVM } from './CustomSettingsHelpers';
import { CustomSettingsFM, DiseaseGroupingVM, DiseaseMatrixFM } from './CustomSettingsModels';
import {
  DiseaseRowFormikControl,
  DiseaseRowGroupAccordian,
  DiseaseTableHeading,
} from './FormikControls.DiseaseTable';

type DiseaseRelevanceMatrixFormikControlProps = {
  formikProps: FormikProps<DiseaseMatrixFM>;
  diseases: NumericDictionary<dto.DiseaseInformationModel>;
  diseaseGroupings: dto.DiseaseGroupModel[];
};

export const DiseaseRelevanceMatrixFormikControl: React.FC<
  DiseaseRelevanceMatrixFormikControlProps
> = ({ formikProps, diseaseGroupings, diseases }) => {
  const [selectedDiseaseGrouping, setSelectedDiseaseGrouping] =
    useState<dto.DiseaseGroupModel>(null);
  const [diseaseSearchFilterText, setDiseaseSearchFilterText] = useState('');

  const vmDiseaseGroupRoot: DiseaseGroupingVM = useMemo(() => {
    return (
      selectedDiseaseGrouping &&
      selectedDiseaseGrouping.subGroups &&
      diseases &&
      Map2DiseaseGroupingVM(selectedDiseaseGrouping.subGroups, diseases, diseaseSearchFilterText)
    );
  }, [selectedDiseaseGrouping, diseases, diseaseSearchFilterText]);
  const vmDiseaseGroupRootHasResults = useMemo(() => {
    function ComputeHasValues(group: DiseaseGroupingVM): boolean {
      return (
        group?.diseases?.length > 0 ||
        !!(group.subgroups && group.subgroups.find(x => ComputeHasValues(x)))
      );
    }
    return !!(vmDiseaseGroupRoot && ComputeHasValues(vmDiseaseGroupRoot));
  }, [vmDiseaseGroupRoot]);

  useEffect(() => {
    diseaseGroupings && diseaseGroupings[0] && setSelectedDiseaseGrouping(diseaseGroupings[0]);
  }, [diseaseGroupings]);

  const { values, getFieldHelpers } = formikProps;

  return (
    <div
      sx={{
        margin: `10px 0`,
        width: `100%`,
        minHeight: `770px`,
        // maxWidth: `600px`,
        backgroundColor: `#fff`,
        padding: `20px 15px`,
        border: `1px solid #E9E9E9`,
        borderRadius: `6px`,
        overflow: `hidden`,

        // CODE: ed8635ac: see FormikControls.DiseaseTable for className's
        '& .disease-row': {
          borderBottom: `1px solid #EAEAEA`,
          '&.disease-group-row': {
            backgroundColor: sxtheme(t => t.colors.deepSea70),
            borderTop: `2px solid #D2D2D2`,
            cursor: `pointer`,
            '&.secondary': {
              backgroundColor: sxtheme(t => t.colors.deepSea20),
              borderTop: `2px solid #D2D2D2`,
            },
            '&:not(.secondary) .disease-name': {
              pl: '15px',
            },
          },
          '&.disease-table-header': {
            borderTop: `2px solid #D2D2D2`,
            borderBottom: `none`,
            position: 'sticky', // TODO: 452806ed: sticky header does not work, possible cause: overflow-x is in on but no scrollbar on page...
            '& .disease-header-text': {
              width: `100%`,
              display: `table-cell`,
              color: `#2D3040`,
              fontStyle: `normal`,
              fontWeight: `normal`,
              fontSize: `14px`,
              lineHeight: `17px`,
              padding: `15px 0 10px`,
              verticalAlign: `bottom`,
            },
            '& .disease-option': {
              verticalAlign: `bottom`,
            },
          },
          '& .disease-name': {
            width: `100%`,
            display: `table-cell`,
            color: `#8C8C8C`,
            fontSize: `16px`,
            lineHeight: `20px`,
            padding: `10px 15px 10px 25px`,
            verticalAlign: `middle`,
          },
          '& .disease-option': {
            minWidth: `75px`,
            borderLeft: `1px solid #EAEAEA`,
            display: `table-cell`,
            textAlign: `center`,
            verticalAlign: `middle`,
            cursor: 'pointer',

            '& .label': {
              fontSize: '12px',
              fontWeight: 400,
              display: 'block',
            },
          },
        },
        '& .not-found': {
          my: '240px',
          textAlign: 'center',
        },
      }}
    >
      <BdSearch
        placeholder='E.g. "measles", "ebola", "zika"'
        debounceDelay={200}
        onSearchTextChange={setDiseaseSearchFilterText}
      />
      {diseaseGroupings && diseaseGroupings.length > 1 && (
        <FlexGroup
          prefix={
            <Typography variant="subtitle2" color="stone90">
              Group by
            </Typography>
          }
        >
          <BdDropdown
            placeholder="Group by"
            options={diseaseGroupings.map((dg, dgIndex) => ({
              text: dg.groupName,
              value: dgIndex,
            }))}
            value={diseaseGroupings.indexOf(selectedDiseaseGrouping)}
            onChange={value => setSelectedDiseaseGrouping(diseaseGroupings[value as number])}
            sx={{
              '&.ui.dropdown': {
                borderBottom: `none`,
              },
            }}
          />
        </FlexGroup>
      )}
      <DiseaseTableHeading
        diseaseGroupRoot={vmDiseaseGroupRoot}
        diseaseFormData={values.diseasesPerRole[values.roleId]}
        getFieldHelpers={getFieldHelpers}
        activeRoleId={values.roleId}
        umbrellaSelectionDisabled={!!diseaseSearchFilterText && diseaseSearchFilterText.length > 0}
      />
      {vmDiseaseGroupRoot &&
        vmDiseaseGroupRoot.subgroups.map(
          diseaseGroup =>
            !!(
              (diseaseGroup.diseases && diseaseGroup.diseases.length) ||
              (diseaseGroup.subgroups && diseaseGroup.subgroups.length)
            ) && (
              <DiseaseRowGroupAccordian
                key={`diseaseGroup-${diseaseGroup.name}`}
                title={diseaseGroup.name}
                diseaseGroup={diseaseGroup}
                diseaseFormData={values.diseasesPerRole[values.roleId]}
                getFieldHelpers={getFieldHelpers}
                activeRoleId={values.roleId}
                umbrellaSelectionDisabled={
                  !!diseaseSearchFilterText && diseaseSearchFilterText.length > 0
                }
              >
                {diseaseGroup.subgroups &&
                  diseaseGroup.subgroups.map(diseaseGroup2 => (
                    <DiseaseRowGroupAccordian
                      key={`diseaseGroup-${diseaseGroup2.name}`}
                      isSecondary
                      title={diseaseGroup2.name}
                      diseaseGroup={diseaseGroup2}
                      diseaseFormData={values.diseasesPerRole[values.roleId]}
                      getFieldHelpers={getFieldHelpers}
                      activeRoleId={values.roleId}
                      umbrellaSelectionDisabled={
                        !!diseaseSearchFilterText && diseaseSearchFilterText.length > 0
                      }
                    >
                      {diseaseGroup2.diseases &&
                        diseaseGroup2.diseases.map(disease => (
                          <DiseaseRowFormikControl
                            key={`diseaseGroup-${diseaseGroup2.name}.${disease.id}`}
                            // prettier-ignore
                            name={`${nameof<CustomSettingsFM>('diseasesPerRole')}.${values.roleId}.${disease.id}`}
                            disease={disease}
                          />
                        ))}
                    </DiseaseRowGroupAccordian>
                  ))}
                {/* DESIGN: 859d1084: if subgroups present do NOT show immediate children diseases, show subgroups only */}
                {!diseaseGroup.subgroups &&
                  diseaseGroup.diseases &&
                  diseaseGroup.diseases.map(disease => (
                    <DiseaseRowFormikControl
                      key={`diseaseGroup-${diseaseGroup.name}.${disease.id}`}
                      // prettier-ignore
                      name={`${nameof<CustomSettingsFM>('diseasesPerRole')}.${values.roleId}.${disease.id}`}
                      disease={disease}
                    />
                  ))}
              </DiseaseRowGroupAccordian>
            )
        )}
      {!vmDiseaseGroupRootHasResults && (
        <div className="not-found">
          <Typography variant="h4" color="stone90">
            No matching diseases.
          </Typography>
        </div>
      )}
    </div>
  );
};
